import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, MapPin, Route, Trash2, X } from 'lucide-react'
import { Vehicle } from '../types'
import { apiClient } from '../config/api'
import { vehicleService } from '../services/vehicleService'

interface Props {
  vehicle: Vehicle
  onClose: () => void
  onSaved: () => void
}

interface CorridorOption {
  id: string
  name: string
  type?: string
}

interface PlaceOption {
  name: string
  lat: number
  lng: number
  origin: string
}

/**
 * Assign a transport to a vehicle: either an itinerary (existing trajectory)
 * or a departure + destination pair picked from the derived places list
 * (trajectory endpoints + geofences).
 */
const TransportAssignmentModal: React.FC<Props> = ({ vehicle, onClose, onSaved }) => {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'itinerary' | 'places'>(vehicle.transport?.corridorId ? 'itinerary' : 'places')
  const [corridors, setCorridors] = useState<CorridorOption[]>([])
  const [places, setPlaces] = useState<PlaceOption[]>([])
  const [corridorId, setCorridorId] = useState<string>(vehicle.transport?.corridorId || '')
  const [departure, setDeparture] = useState<string>(vehicle.transport?.departure || '')
  const [destination, setDestination] = useState<string>(vehicle.transport?.destination || '')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [vehicleSource, vehicleIdRaw] = vehicle.id.split('-')
  const vehicleId = Number(vehicleIdRaw)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      apiClient.get('/corridors').catch(() => ({ data: [] })),
      vehicleService.getPlaces().catch(() => [])
    ]).then(([corridorResponse, placeRows]) => {
      if (cancelled) return
      const rows = Array.isArray(corridorResponse.data) ? corridorResponse.data : []
      setCorridors(rows
        .filter((row: any) => !row.type || String(row.type).toLowerCase() === 'trajectory')
        .map((row: any) => ({ id: String(row.id), name: row.name, type: row.type })))
      setPlaces(placeRows)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  const placeByName = useMemo(() => new Map(places.map(place => [place.name, place])), [places])

  const canSave = mode === 'itinerary'
    ? Boolean(corridorId)
    : Boolean(departure && destination && departure !== destination)

  const save = async () => {
    if (!canSave || saving) return
    setSaving(true)
    setError(null)
    try {
      if (mode === 'itinerary') {
        await vehicleService.setTransport(vehicleSource as 'ASSET' | 'CONTAINER', vehicleId, { corridor_id: corridorId })
      } else {
        const dep = placeByName.get(departure)
        const dest = placeByName.get(destination)
        if (!dep || !dest) throw new Error(t('transport_pick_places', { defaultValue: 'Pick a departure and a destination' }))
        await vehicleService.setTransport(vehicleSource as 'ASSET' | 'CONTAINER', vehicleId, {
          departure: { name: dep.name, lat: dep.lat, lng: dep.lng },
          destination: { name: dest.name, lat: dest.lat, lng: dest.lng }
        })
      }
      onSaved()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || t('transport_save_failed', { defaultValue: 'Failed to save transport' }))
    } finally {
      setSaving(false)
    }
  }

  const clearTransport = async () => {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      await vehicleService.setTransport(vehicleSource as 'ASSET' | 'CONTAINER', vehicleId, { clear: true })
      onSaved()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || t('transport_save_failed', { defaultValue: 'Failed to save transport' }))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[320] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden"
        onClick={event => event.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">
              {t('transport_assign_title', { defaultValue: 'Assign transport' })}
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{vehicle.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-1.5 p-1 rounded-xl bg-slate-100 dark:bg-slate-800">
            <button
              onClick={() => setMode('itinerary')}
              className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold ${
                mode === 'itinerary'
                  ? 'bg-white dark:bg-slate-700 text-blue-700 dark:text-blue-300 shadow-sm'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              <Route size={14} /> {t('transport_mode_itinerary', { defaultValue: 'Itinerary' })}
            </button>
            <button
              onClick={() => setMode('places')}
              className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold ${
                mode === 'places'
                  ? 'bg-white dark:bg-slate-700 text-blue-700 dark:text-blue-300 shadow-sm'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              <MapPin size={14} /> {t('transport_mode_places', { defaultValue: 'Departure / destination' })}
            </button>
          </div>

          {loading ? (
            <div className="py-8 flex items-center justify-center text-slate-400">
              <Loader2 size={18} className="animate-spin" />
            </div>
          ) : mode === 'itinerary' ? (
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                {t('transport_itinerary', { defaultValue: 'Itinerary (trajectory)' })}
              </label>
              <select
                value={corridorId}
                onChange={event => setCorridorId(event.target.value)}
                className="w-full h-10 px-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-100"
              >
                <option value="">{t('transport_select_itinerary', { defaultValue: 'Select a trajectory…' })}</option>
                {corridors.map(corridor => (
                  <option key={corridor.id} value={corridor.id}>{corridor.name}</option>
                ))}
              </select>
              {corridors.length === 0 && (
                <p className="text-[11px] text-slate-500">
                  {t('transport_no_itineraries', { defaultValue: 'No trajectories defined yet — create one in Trajectories.' })}
                </p>
              )}
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                {t('transport_itinerary_hint', { defaultValue: 'Departure and destination are taken from the trajectory endpoints; progress follows the route.' })}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  {t('transport_departure', { defaultValue: 'Departure' })}
                </label>
                <select
                  value={departure}
                  onChange={event => setDeparture(event.target.value)}
                  className="w-full h-10 px-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-100"
                >
                  <option value="">{t('transport_select_place', { defaultValue: 'Select a place…' })}</option>
                  {places.map(place => (
                    <option key={place.name} value={place.name}>{place.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  {t('transport_destination', { defaultValue: 'Destination' })}
                </label>
                <select
                  value={destination}
                  onChange={event => setDestination(event.target.value)}
                  className="w-full h-10 px-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-100"
                >
                  <option value="">{t('transport_select_place', { defaultValue: 'Select a place…' })}</option>
                  {places.map(place => (
                    <option key={place.name} value={place.name}>{place.name}</option>
                  ))}
                </select>
              </div>
              {places.length === 0 && (
                <p className="text-[11px] text-slate-500">
                  {t('transport_no_places', { defaultValue: 'No places yet — places come from trajectory endpoints and geofences.' })}
                </p>
              )}
            </div>
          )}

          {error && <p className="text-[11px] font-semibold text-red-600">{error}</p>}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
          {vehicle.transport ? (
            <button
              onClick={clearTransport}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-50"
            >
              <Trash2 size={13} /> {t('transport_clear', { defaultValue: 'Clear transport' })}
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-2 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
              {t('cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              onClick={save}
              disabled={!canSave || saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50"
            >
              {saving && <Loader2 size={13} className="animate-spin" />}
              {t('transport_save', { defaultValue: 'Assign' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TransportAssignmentModal
